import { useCallback, useState } from "react";
import Hubspot from "@/assets/crm/hubspot.svg";
import { CRMPlatform } from "@/enums/CRM.enum";
import { capitalizeFirstLetter } from "@/utils/helper.util";
import { CrmPlatformStatusDto } from "@advanced-sales-technologies/backend-client/models/CrmPlatformStatusDto";
import { useTranslation } from "react-i18next";
import { HiOutlineArrowRight } from "react-icons/hi2";

import { useCRMConnect } from "@/hooks/api/crm/useCRMConnect";
import { useCRMConnectionsStatus } from "@/hooks/api/crm/useCRMConnectionsStatus";
import { useConnectListCRM } from "@/hooks/api/list/crm/useConnectListCRM";
import { useCRMConnectionListStatus } from "@/hooks/api/list/crm/useCRMConnectionListStatus";
import Button from "@/components/atoms/Button";
import Card from "@/components/atoms/Card";
import Modal from "@/components/molecules/Modal";
import CRMDisconnectListModal from "@/components/organisms/crm/CRMDisconnectListModal";
import CRMRemoveConnectionModal from "@/components/organisms/crm/CRMRemoveConnectionModal";

type Props = {
  listId?: string;
  listName?: string;
  className?: string;
  onClose?: () => void;
};

const AllCRMGrid = ({ listId, listName, className, onClose }: Props) => {
  const { t } = useTranslation();
  const [disconnectPlatform, setDisconnectPlatform] = useState<CRMPlatform | null>(null);
  const [disconnectList, setDisconnectList] = useState<boolean>(false);

  const { data: listCRMConnectionStatus } = useCRMConnectionListStatus({ id: listId });
  const { data: CRMConnections } = useCRMConnectionsStatus({});

  const { connectCRM, isPending: connectCRMLoading } = useCRMConnect();
  const { connectListToCRM, isPending: connectListToCRMLoading } = useConnectListCRM();

  const platformButton = useCallback(
    ({ platform, status }: { platform: CRMPlatform; status: CrmPlatformStatusDto | undefined }) => {
      if (!status) {
        return (
          <Button
            label={<div className={"h-3 w-32 animate-pulse rounded-full bg-gray-300"} />}
            variants={"primary"}
            color={"natural"}
            disabled
            className={"!bg-gray-400"}
          />
        );
      }

      if (!status.connected) {
        return (
          <Button
            label={t("crm.connections.setup")}
            variants={"secondary"}
            isLoading={connectCRMLoading}
            trailingIcon={<HiOutlineArrowRight className={"size-5"} />}
            onClick={() => connectCRM({ listId, platform })}
          />
        );
      }

      if (status.connected) {
        if (listId) {
          if (listCRMConnectionStatus?.hubspot?.connected) {
            return (
              <Button
                label={t("crm.connections.disconnect.title")}
                variants={"secondary"}
                color={"error"}
                trailingIcon={<HiOutlineArrowRight className={"size-5"} />}
                onClick={() => setDisconnectList(true)}
              />
            );
          }
          return (
            <Button
              label={t("crm.connections.connectList")}
              isLoading={connectListToCRMLoading}
              variants={"secondary"}
              trailingIcon={<HiOutlineArrowRight className={"size-5"} />}
              onClick={() => {
                connectListToCRM({ listId, platform });
              }}
            />
          );
        }

        return (
          <Button
            label={t("crm.connections.remove.title", { platform: capitalizeFirstLetter(platform) })}
            variants={"secondary"}
            color={"error"}
            onClick={() => setDisconnectPlatform(platform)}
          />
        );
      }
    },
    [
      t,
      connectCRM,
      connectListToCRM,
      listId,
      listCRMConnectionStatus,
      disconnectPlatform,
      disconnectList,
      connectListToCRMLoading,
      connectCRMLoading,
    ],
  );

  return (
    <div className={className}>
      <Card
        icon={<img src={Hubspot} alt={CRMPlatform.HUBSPOT} className={"size-10"} />}
        title={capitalizeFirstLetter(CRMPlatform.HUBSPOT)}
        description={t("crm.connections.platform", { platform: capitalizeFirstLetter(CRMPlatform.HUBSPOT) })}
      >
        {platformButton({ platform: CRMPlatform.HUBSPOT, status: CRMConnections?.hubspot })}
      </Card>

      <Modal
        isOpen={!!disconnectPlatform}
        onClose={() => setDisconnectPlatform(null)}
        content={CRMRemoveConnectionModal}
        contentProps={{ onSuccess: () => setDisconnectPlatform(null), platform: disconnectPlatform! }}
      />

      <Modal
        isOpen={disconnectList}
        onClose={() => setDisconnectList(false)}
        content={CRMDisconnectListModal}
        contentProps={{
          onSuccess: () => {
            onClose?.();
            setDisconnectList(false);
          },
          platform: disconnectPlatform || CRMPlatform.HUBSPOT,
          listId: listId!,
          listName: listName!,
        }}
      />
    </div>
  );
};

export default AllCRMGrid;
